import React, { Component } from "react";
import * as Cookies from "es-cookie";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import SelectDropDown from "../selectdropdown/SelectDropDown";
import { pointTooltip } from "./chartTooltip";
import ChartBoundary from "./ChartBoundary";
import { PageShimmer } from "./Shimmer";

// Self-contained modern Complexity tab. Queries getComplexityAnalysisData
// directly (the old shared ComplexityAnalysisSection is used elsewhere, so it is
// NOT touched) and renders difficulty-level performance in the profile's modern
// style: KPI tiles → accuracy-by-difficulty bar chart → subject heatmap → q-type.

// College bridge: the viewed student comes from props (route state). Different
// entry points name the mobile field differently, so check every known variant.
// The student-app tab keys on mobile.
const resolveMobile = (student) => {
  if (!student) return null;
  return (
    student.contact_no || student.student_mobile || student.mobile ||
    student.studentMobile || student.parent_mobile || student.phone ||
    student.mobile_no || student.username || null
  );
};

// College view: this profile shows ANOTHER student, so "You/Your" becomes the
// student's (first) name. Falls back to "Student" if no name.
const studentName = (student) => {
  const full = (student && (student.student_name || student.name)) || "";
  return full.trim() ? full.trim() : "Student";
};
const studentFirstName = (student) => studentName(student).split(/\s+/)[0];

const FETCH_COMPLEXITY = gql`
  query ($mobile: String!, $exam_type: String!, $class_id: String!) {
    getComplexityAnalysisData(mobile: $mobile, exam_type: $exam_type, class_id: $class_id) {
      class_id
      complexity_data { complexity correct skipped error }
      class_wise_complexity {
        class_id
        practice_complexity { complexity accuracy correct error }
        exam_complexity { complexity accuracy correct skipped error }
      }
      subject_complexity {
        subject
        accuracy
        complexity_wise_accuracy { complexity practice_accuracy exam_accuracy }
      }
      qtype_complexity {
        qtype
        complexity_accuracy { complexity total accuracy }
      }
    }
  }
`;

// The four difficulty levels (exact, case-sensitive) + display config.
// iconBg mirrors the gradient-chip style used by the KPI cards on the other tabs.
const LEVELS = [
  { key: "Easy", label: "Easy", color: "#16a34a", icon: "fa-feather", iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" },
  { key: "Moderate", label: "Moderate", color: "#ca8a04", icon: "fa-wave-square", iconBg: "linear-gradient(135deg, #facc15 0%, #ca8a04 100%)" },
  { key: "Difficult", label: "Difficult", color: "#ea580c", icon: "fa-fire", iconBg: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" },
  { key: "Highly Difficult", label: "Highly Difficult", color: "#dc2626", icon: "fa-bolt", iconBg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
];

const EXAM_OPTIONS = [
  { value: "0,1", label: "All" },
  { value: "0", label: "Practice" },
  { value: "1", label: "Exam" },
];
const CLASS_OPTIONS = [
  { value: "1,2", label: "All classes" },
  { value: "1", label: "Class XI" },
  { value: "2", label: "Class XII" },
];

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Accuracy → heat color (weak = red, strong = green).
const heatColor = (pct) => {
  const p = num(pct);
  if (p <= 0) return "#f1f5f9";
  if (p < 40) return "#fee2e2";
  if (p < 60) return "#ffedd5";
  if (p < 75) return "#fef9c3";
  if (p < 90) return "#dcfce7";
  return "#bbf7d0";
};
const heatText = (pct) => (num(pct) <= 0 ? "#94a3b8" : "#1f2937");

class ComplexityAnalysisTab extends Component {
  state = {
    loading: true,
    data: null,
    examType: "0,1", // 0,1 = all
    classId: "1,2",  // 1,2 = all
    mode: "exam",    // exam | practice — which series the KPI/heatmap emphasises
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.examStream !== this.props.examStream) this.load();
  }

  load = async () => {
    this.setState({ loading: true });
    const mobile = `${resolveMobile(this.props.student) || ""}`;
    try {
      const res = await this.props.client.query({
        query: FETCH_COMPLEXITY,
        variables: { mobile, exam_type: this.state.examType, class_id: this.state.classId },
        fetchPolicy: "network-only",
      });
      this.setState({ data: res?.data?.getComplexityAnalysisData || [], loading: false });
    } catch (e) {
      console.log(e);
      this.setState({ data: [], loading: false });
    }
  };

  onExamChange = (_n, value) => this.setState({ examType: value }, this.load);
  onClassChange = (_n, value) => this.setState({ classId: value }, this.load);

  // Pick the single class record + its class_wise_complexity for the selection.
  getScoped = () => {
    const { data, classId } = this.state;
    if (!data || !data.length) return null;
    const record = classId === "1,2"
      ? data.find((d) => String(d.class_id) === "0")
      : data.find((d) => String(d.class_id) === String(classId));
    if (!record) return null;
    const classn = classId === "2" ? "2" : "1";
    const cw = (record.class_wise_complexity || []).find((a) => String(a.class_id) === classn) || {};
    return { record, cw };
  };

  // Merge practice + exam accuracy per difficulty level for KPIs + bar chart.
  levelSeries = (cw) => {
    const byLevel = {};
    LEVELS.forEach((l) => (byLevel[l.key] = { practice: 0, exam: 0, pc: 0, pe: 0 }));
    (cw.practice_complexity || []).forEach((r) => {
      if (byLevel[r.complexity]) { byLevel[r.complexity].practice = num(r.accuracy); byLevel[r.complexity].pc = 1; }
    });
    (cw.exam_complexity || []).forEach((r) => {
      if (byLevel[r.complexity]) { byLevel[r.complexity].exam = num(r.accuracy); byLevel[r.complexity].pe = 1; }
    });
    return byLevel;
  };

  renderFilters = () => {
    const selExam = EXAM_OPTIONS.find((o) => o.value === this.state.examType) || EXAM_OPTIONS[0];
    const selClass = CLASS_OPTIONS.find((o) => o.value === this.state.classId) || CLASS_OPTIONS[0];
    return (
      <div className="d-flex flex-wrap align-items-center justify-content-end mb-3" style={{ gap: 10 }}>
        <div style={{ minWidth: 150 }}>
          <SelectDropDown stateData={selExam} options={EXAM_OPTIONS} name="examType" placeholderName="Type" handleChange={this.onExamChange} />
        </div>
        <div style={{ minWidth: 150 }}>
          <SelectDropDown stateData={selClass} options={CLASS_OPTIONS} name="classId" placeholderName="Class" handleChange={this.onClassChange} />
        </div>
      </div>
    );
  };

  // KPI tiles — same style as the other tabs (label + big value + sub-line +
  // gradient icon chip). Shows exam accuracy per difficulty (falls back to practice).
  renderKpis = (byLevel) => (
    <div className="sp-stat-grid mb-4">
      {LEVELS.map((l) => {
        const v = byLevel[l.key];
        const primary = v.pe ? v.exam : v.practice;
        const noteText = v.pe && v.pc ? `practice ${Math.round(v.practice)}%` : v.pe ? "exam accuracy" : "practice accuracy";
        return (
          <div
            key={l.key}
            className="sp-summary-card d-flex align-items-start justify-content-between p-3 h-100"
            style={{ borderRadius: "16px", background: "#ffffff", border: "1px solid #f1f2f5", boxShadow: "0 6px 18px rgba(0,0,0,0.05)" }}
          >
            <div>
              <span className="d-block text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>{l.label}</span>
              <strong className="d-block text-dark" style={{ fontSize: "2rem", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>
                {Math.round(primary)}%
              </strong>
              <span className="d-block" style={{ fontSize: "12px", fontWeight: 600, color: l.color }}>
                <i className={`fas ${v.pe ? "fa-graduation-cap" : "fa-dumbbell"} mr-1`} style={{ fontSize: 10 }} />{noteText}
              </span>
            </div>
            <div
              className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
              style={{ width: "44px", height: "44px", borderRadius: "12px", background: l.iconBg, boxShadow: "0 6px 14px rgba(0,0,0,0.12)" }}
            >
              <i className={`fas ${l.icon}`} style={{ fontSize: "18px" }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  // Grouped bar: accuracy by difficulty, Practice vs Exam.
  renderBarChart = (byLevel) => {
    const categories = LEVELS.map((l) => l.label);
    const practice = LEVELS.map((l) => Math.round(byLevel[l.key].practice));
    const exam = LEVELS.map((l) => Math.round(byLevel[l.key].exam));
    const hasPractice = LEVELS.some((l) => byLevel[l.key].pc);
    const hasExam = LEVELS.some((l) => byLevel[l.key].pe);
    const series = [];
    if (hasPractice) series.push({ name: "Practice", data: practice, color: "#818cf8" });
    if (hasExam) series.push({ name: "Exam", data: exam, color: "#4f46e5" });
    if (!series.length) return null;

    const options = {
      chart: { type: "column", backgroundColor: "transparent", height: 300, style: { fontFamily: "'Outfit', sans-serif" } },
      title: { text: "" },
      credits: { enabled: false },
      legend: { enabled: series.length > 1 },
      xAxis: { categories, lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
      yAxis: { min: 0, max: 100, title: { text: "Accuracy %", style: { color: "#94a3b8", fontSize: "11px" } }, gridLineColor: "#f1f5f9", labels: { style: { color: "#94a3b8" } } },
      tooltip: pointTooltip({ unit: "%" }),
      plotOptions: { column: { borderRadius: 4, borderWidth: 0, groupPadding: 0.12, dataLabels: { enabled: true, format: "{y}%", style: { fontSize: "10px", fontWeight: "700", color: "#475569", textOutline: "none" } } } },
      series,
    };
    return (
      <div className="p-3 mb-4" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fff" }}>
        <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <i className="fas fa-chart-column mr-2" style={{ color: "#4f46e5" }} />Accuracy by Difficulty
        </h6>
        <span className="text-muted d-block mb-2" style={{ fontSize: 11.5 }}>Practice vs Exam accuracy across the four difficulty levels.</span>
        <ChartBoundary><HighchartsReact highcharts={Highcharts} options={options} /></ChartBoundary>
      </div>
    );
  };

  // Subject × difficulty heatmap (exam accuracy; practice shown in title).
  renderSubjectHeatmap = (subjects) => {
    if (!subjects || !subjects.length) return null;
    return (
      <div className="p-3 mb-4" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fff" }}>
        <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <i className="fas fa-table-cells mr-2" style={{ color: "#0ea5e9" }} />Subject × Difficulty
        </h6>
        <span className="text-muted d-block mb-3" style={{ fontSize: 11.5 }}>Exam accuracy per subject at each difficulty — darker green is stronger, red is weaker.</span>
        <div style={{ overflowX: "auto" }}>
          <table className="sp-cx-heat" style={{ borderCollapse: "separate", borderSpacing: 6, minWidth: 520 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", fontSize: 11, color: "#94a3b8", fontWeight: 700, padding: "0 8px" }}>SUBJECT</th>
                {LEVELS.map((l) => (
                  <th key={l.key} style={{ fontSize: 11, color: l.color, fontWeight: 700, textAlign: "center", minWidth: 92 }}>{l.label}</th>
                ))}
                <th style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textAlign: "center" }}>OVERALL</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((s) => {
                const cellByLevel = {};
                (s.complexity_wise_accuracy || []).forEach((c) => { cellByLevel[c.complexity] = c; });
                return (
                  <tr key={s.subject}>
                    <td style={{ fontWeight: 700, fontSize: 13, color: "#334155", padding: "0 8px", whiteSpace: "nowrap" }}>{s.subject}</td>
                    {LEVELS.map((l) => {
                      const c = cellByLevel[l.key] || {};
                      const exam = num(c.exam_accuracy);
                      const practice = num(c.practice_accuracy);
                      return (
                        <td key={l.key}
                          title={`${s.subject} · ${l.label} — exam ${Math.round(exam)}%, practice ${Math.round(practice)}%`}
                          style={{ background: heatColor(exam), color: heatText(exam), borderRadius: 8, textAlign: "center", padding: "10px 6px", fontWeight: 700, fontSize: 13 }}>
                          {exam > 0 ? `${Math.round(exam)}%` : "—"}
                        </td>
                      );
                    })}
                    <td style={{ textAlign: "center", fontWeight: 800, fontSize: 13, color: "#4f46e5" }}>{Math.round(num(s.accuracy))}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Question-type × difficulty table (counts + accuracy).
  renderQtypeTable = (qtypes) => {
    if (!qtypes || !qtypes.length) return null;
    const rows = qtypes
      .map((q) => {
        const byLevel = {};
        (q.complexity_accuracy || []).forEach((c) => { byLevel[c.complexity] = c; });
        const anyData = LEVELS.some((l) => byLevel[l.key] && (num(byLevel[l.key].total) > 0));
        return anyData ? { qtype: q.qtype, byLevel } : null;
      })
      .filter(Boolean);
    if (!rows.length) return null;
    return (
      <div className="p-3" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fff" }}>
        <h6 className="font-weight-bold text-dark mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <i className="fas fa-list-check mr-2" style={{ color: "#8b5cf6" }} />Question Type × Difficulty
        </h6>
        <div className="sp-table-wrap">
          <table className="sp-doubts-table" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>Question Type</th>
                {LEVELS.map((l) => <th key={l.key} className="text-center">{l.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.qtype} style={{ cursor: "default" }}>
                  <td style={{ fontWeight: 700, color: "#334155" }}>{r.qtype}</td>
                  {LEVELS.map((l) => {
                    const c = r.byLevel[l.key];
                    if (!c || num(c.total) <= 0) return <td key={l.key} className="text-center text-muted">—</td>;
                    return (
                      <td key={l.key} className="text-center">
                        <span style={{ fontWeight: 700, color: heatText(c.accuracy) }}>{Math.round(num(c.accuracy))}%</span>
                        <span className="text-muted d-block" style={{ fontSize: 10.5 }}>{num(c.total)} qs</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  render() {
    const { loading } = this.state;
    const scoped = this.getScoped();
    const cw = scoped ? scoped.cw : {};
    const record = scoped ? scoped.record : {};
    const byLevel = this.levelSeries(cw);
    const hasAny = scoped && (
      (cw.practice_complexity && cw.practice_complexity.length) ||
      (cw.exam_complexity && cw.exam_complexity.length) ||
      (record.subject_complexity && record.subject_complexity.length)
    );

    return (
      <div className="sp-content-card" style={{ marginTop: 0 }}>
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-1" style={{ gap: 10 }}>
          <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <i className="far fa-layer-group mr-2" style={{ color: "#4f46e5" }} />Complexity Analysis
          </h6>
        </div>
        <span className="text-muted d-block mb-3" style={{ fontSize: 12.5 }}>Performance across difficulty levels — where the student is strong and where accuracy drops.</span>

        {loading ? (
          <PageShimmer />
        ) : !hasAny ? (
          <>
            {this.renderFilters()}
            <div className="text-center text-muted py-5" style={{ fontSize: 14 }}>
              <i className="far fa-layer-group d-block mb-2" style={{ fontSize: 30, color: "#cbd5e1" }} />
              No complexity data for this selection.
            </div>
          </>
        ) : (
          <>
            {this.renderFilters()}
            {this.renderKpis(byLevel)}
            {this.renderBarChart(byLevel)}
            {this.renderSubjectHeatmap(record.subject_complexity)}
            {this.renderQtypeTable(record.qtype_complexity)}
          </>
        )}
      </div>
    );
  }
}

export default compose(withApollo, withRouter)(ComplexityAnalysisTab);
